import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getPersonnel,
  setScreenPermission,
  updatePersonnel,
} from '../../../services/migration/personnelDataService';
import {
  CourierKind,
  CourierProfile,
  CourierProfileRequest,
  VALET_PROVIDERS,
  ValetProvider,
  courierModuleService,
  providerLabel,
} from './courierModuleService';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Kendi kuryemiz seçilince bugünkü personel ekleme penceresi açılır. */
  onOwnSelected: () => void;
  /** Kayıt sonrası; openAgreement true ise anlaşma ekranı açılır. */
  onCreated: (courier: CourierProfile, openAgreement: boolean) => void;
  existing: CourierProfile[];
}

interface PersonOption {
  uid: string;
  name: string;
  phone: string;
}

const KINDS: CourierKind[] = ['OWN', 'FREELANCE', 'COMPANY', 'PLATFORM_VALET', 'INTEGRATION'];

// Uygulamayı kullanacak dış kurye / firma elemanı için varsayılan kurye yetkileri (AddCourierModal ile aynı)
const DEFAULT_APP_PERMISSIONS = {
  accessCourierApp: true,
  viewAssignedOrders: true,
  updateOrderStatus: true,
  viewCustomerInfo: true,
  callCustomer: true,
  viewOrderDetails: true,
  markAsDelivered: true,
  reportIssues: true,
  viewEarnings: true,
};

const AddCourierWizard: React.FC<Props> = ({ open, onClose, onOwnSelected, onCreated, existing }) => {
  const { t } = useTranslation('salesSystem');
  const { userDetails, branches } = useAuth();
  const companyId = userDetails?.companyId;

  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<CourierKind | null>(null);
  const [companyMode, setCompanyMode] = useState<'firm' | 'employee'>('firm');
  const [parentId, setParentId] = useState<number | null>(null);
  const [provider, setProvider] = useState<ValetProvider | null>(null);
  const [usesApp, setUsesApp] = useState(false);
  const [person, setPerson] = useState<PersonOption | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [iban, setIban] = useState('');
  const [plate, setPlate] = useState('');
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [collectsCash, setCollectsCash] = useState(true);
  const [paidFromCash, setPaidFromCash] = useState(false);

  const [people, setPeople] = useState<PersonOption[]>([]);
  const [valetBranches, setValetBranches] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setKind(null);
    setCompanyMode('firm');
    setParentId(null);
    setProvider(null);
    setUsesApp(false);
    setPerson(null);
    setName('');
    setPhone('');
    setIban('');
    setPlate('');
    setBranchIds(branches.length === 1 ? [branches[0].id] : []);
    setCollectsCash(true);
    setPaidFromCash(false);
    setError(null);
    courierModuleService.valetBranches().then(setValetBranches).catch(() => setValetBranches({}));
  }, [open, branches]);

  // Uygulamayı kullanacak kurye için personel listesi (zaten kurye olanlar hariç)
  useEffect(() => {
    if (!open || !usesApp || !companyId || people.length > 0) return;
    const taken = new Set(existing.map((c) => c.firebaseUid));
    getPersonnel(companyId)
      .then((list: any[]) =>
        setPeople(
          (list || [])
            .filter((p) => p?.id && !taken.has(p.id))
            .map((p) => ({ uid: p.id, name: p.name || '—', phone: p.phone || '' }))
            .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
        )
      )
      .catch(() => setPeople([]));
  }, [open, usesApp, companyId, existing, people.length]);

  const firms = useMemo(
    () => existing.filter((c) => c.kind === 'COMPANY' && !c.parentCourierId),
    [existing]
  );

  const isValet = kind === 'PLATFORM_VALET';
  const appAllowed = kind === 'FREELANCE' || (kind === 'COMPANY' && companyMode === 'employee');
  const allowedValetBranches = provider ? valetBranches[provider] || [] : [];

  const steps = [
    t('courierModule.wizard.steps.kind'),
    t('courierModule.wizard.steps.info'),
    t('courierModule.wizard.steps.branches'),
    t('courierModule.wizard.steps.cash'),
  ];

  const chooseKind = (k: CourierKind) => {
    if (k === 'INTEGRATION') return;
    if (k === 'OWN') {
      onClose();
      onOwnSelected();
      return;
    }
    setKind(k);
    setUsesApp(false);
    if (k === 'PLATFORM_VALET') {
      setCollectsCash(false);
      setPaidFromCash(false);
    } else {
      setCollectsCash(true);
    }
    setStep(1);
  };

  const infoError = (): string | null => {
    if (kind === 'PLATFORM_VALET') {
      if (!provider) return t('courierModule.wizard.errors.provider');
      if (!name.trim()) return t('courierModule.wizard.errors.name');
      return null;
    }
    if (kind === 'COMPANY' && companyMode === 'employee' && !parentId) return t('courierModule.wizard.errors.parent');
    if (appAllowed && usesApp) return person ? null : t('courierModule.wizard.errors.person');
    if (!name.trim()) return t('courierModule.wizard.errors.name');
    if (kind === 'FREELANCE' && !phone.trim()) return t('courierModule.wizard.errors.phone');
    return null;
  };

  const branchError = (): string | null => {
    if (branchIds.length === 0) return t('courierModule.wizard.errors.branch');
    if (isValet && branchIds.some((b) => !allowedValetBranches.includes(b)))
      return t('courierModule.wizard.errors.valetBranch', { provider: providerLabel(provider) });
    return null;
  };

  const next = () => {
    const err = step === 1 ? infoError() : step === 2 ? branchError() : null;
    setError(err);
    if (err) return;
    // Valede nakit adımı yok
    if (step === 2 && isValet) {
      void save(true);
      return;
    }
    setStep((s) => s + 1);
  };

  const save = async (openAgreement: boolean) => {
    if (!kind) return;
    const err = infoError() || branchError();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const appUser = appAllowed && usesApp && person;
      const req: CourierProfileRequest = {
        kind,
        providerCode: isValet ? provider : null,
        name: appUser ? null : name.trim(),
        phoneNumber: appUser ? null : phone.trim() || null,
        iban: iban.trim() || null,
        plateNumber: plate.trim() || null,
        branchIds,
        parentCourierId: kind === 'COMPANY' && companyMode === 'employee' ? parentId : null,
        usesApp: !!appUser,
        firebaseUid: appUser ? person!.uid : null,
        collectsCash: isValet ? false : collectsCash,
        paidFromCash: isValet ? false : collectsCash && paidFromCash,
      };
      const created = await courierModuleService.createCourier(req);

      // Uygulamayı kullanacaksa personelin rolü kurye olur ve kurye yetkileri açılır
      if (appUser) {
        await updatePersonnel(person!.uid, { role: 'courier' } as any);
        await setScreenPermission(person!.uid, 'courier', null, true, DEFAULT_APP_PERMISSIONS);
      }

      onCreated(created, openAgreement);
      onClose();
    } catch (e: any) {
      setError(e?.message || t('courierModule.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const kindCard = (k: CourierKind) => {
    const disabled = k === 'INTEGRATION';
    const card = (
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          opacity: disabled ? 0.5 : 1,
          borderWidth: kind === k ? 2 : 1,
          borderColor: kind === k ? 'primary.main' : 'divider',
        }}
      >
        <CardActionArea disabled={disabled} onClick={() => chooseKind(k)} sx={{ p: 1.5, height: '100%', alignItems: 'flex-start' }}>
          <Typography fontWeight={700}>{t(`courierModule.kinds.${k}`)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(`courierModule.wizard.kindHelp.${k}`)}
          </Typography>
          <Chip size="small" sx={{ mt: 1 }} label={t(`courierModule.wizard.kindChip.${k}`)} />
        </CardActionArea>
      </Card>
    );
    return disabled ? (
      <Tooltip key={k} title={t('courierModule.wizard.phase2')}>
        <span>{card}</span>
      </Tooltip>
    ) : (
      <Box key={k}>{card}</Box>
    );
  };

  const personFields = (
    <>
      {appAllowed && (
        <FormControlLabel
          control={<Switch checked={usesApp} onChange={(e) => setUsesApp(e.target.checked)} />}
          label={t('courierModule.wizard.usesApp')}
        />
      )}
      {appAllowed && usesApp ? (
        <>
          <Autocomplete
            options={people}
            value={person}
            onChange={(_, v) => setPerson(v)}
            getOptionLabel={(o) => (o.phone ? `${o.name} · ${o.phone}` : o.name)}
            isOptionEqualToValue={(a, b) => a.uid === b.uid}
            renderInput={(params) => <TextField {...params} label={t('courierModule.wizard.personnel')} required />}
          />
          <Alert severity="info">{t('courierModule.wizard.appUserNote')}</Alert>
        </>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <TextField label={t('courierModule.fields.name')} value={name} onChange={(e) => setName(e.target.value)} required />
          <TextField
            label={t('courierModule.fields.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required={kind === 'FREELANCE'}
          />
          <TextField label={t('courierModule.fields.iban')} value={iban} onChange={(e) => setIban(e.target.value)} />
          <TextField label={t('courierModule.fields.plate')} value={plate} onChange={(e) => setPlate(e.target.value)} />
        </Box>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('courierModule.wizard.title')}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 2 }}>
          {steps.map((s, i) => (
            <Step key={s} completed={step > i}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            {KINDS.map(kindCard)}
          </Box>
        )}

        {step === 1 && kind && (
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              {t(`courierModule.kinds.${kind}`)}
            </Typography>

            {kind === 'FREELANCE' && personFields}

            {kind === 'COMPANY' && (
              <>
                <RadioGroup row value={companyMode} onChange={(e) => setCompanyMode(e.target.value as any)}>
                  <FormControlLabel value="firm" control={<Radio />} label={t('courierModule.wizard.newFirm')} />
                  <FormControlLabel
                    value="employee"
                    control={<Radio />}
                    label={t('courierModule.wizard.firmEmployee')}
                    disabled={firms.length === 0}
                  />
                </RadioGroup>
                {companyMode === 'employee' && (
                  <Autocomplete
                    options={firms}
                    value={firms.find((f) => f.id === parentId) || null}
                    onChange={(_, v) => setParentId(v?.id ?? null)}
                    getOptionLabel={(o) => o.name || '—'}
                    renderInput={(params) => <TextField {...params} label={t('courierModule.wizard.parentFirm')} required />}
                  />
                )}
                {personFields}
                {companyMode === 'firm' && <Alert severity="info">{t('courierModule.wizard.firmNote')}</Alert>}
              </>
            )}

            {kind === 'PLATFORM_VALET' && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                  {VALET_PROVIDERS.map((p) => {
                    const count = (valetBranches[p] || []).length;
                    return (
                      <Card
                        key={p}
                        variant="outlined"
                        sx={{
                          opacity: count === 0 ? 0.5 : 1,
                          borderWidth: provider === p ? 2 : 1,
                          borderColor: provider === p ? 'primary.main' : 'divider',
                        }}
                      >
                        <CardActionArea
                          disabled={count === 0}
                          sx={{ p: 1.5 }}
                          onClick={() => {
                            setProvider(p);
                            setName(t('courierModule.wizard.valetDefaultName', { provider: providerLabel(p) }));
                            setBranchIds((valetBranches[p] || []).slice(0, 1));
                          }}
                        >
                          <Typography fontWeight={700}>{providerLabel(p)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {count > 0
                              ? t('courierModule.wizard.valetBranchCount', { count })
                              : t('courierModule.wizard.valetNoIntegration', { provider: providerLabel(p) })}
                          </Typography>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                </Box>
                <TextField label={t('courierModule.fields.name')} value={name} onChange={(e) => setName(e.target.value)} required />
                <Alert severity="info">{t('courierModule.wizard.valetNote')}</Alert>
              </>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {isValet
                ? t('courierModule.wizard.valetBranchHelp', { provider: providerLabel(provider) })
                : t('courierModule.wizard.branchHelp')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {branches.map((b) => {
                const selected = branchIds.includes(b.id);
                const blocked = isValet && !allowedValetBranches.includes(b.id);
                const chip = (
                  <Chip
                    key={b.id}
                    label={b.name}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    disabled={blocked}
                    onClick={() =>
                      setBranchIds((prev) => (prev.includes(b.id) ? prev.filter((x) => x !== b.id) : [...prev, b.id]))
                    }
                  />
                );
                return blocked ? (
                  <Tooltip key={b.id} title={t('courierModule.wizard.integrationClosed', { provider: providerLabel(provider) })}>
                    <span>{chip}</span>
                  </Tooltip>
                ) : (
                  chip
                );
              })}
            </Box>
          </Stack>
        )}

        {step === 3 && (
          <Stack spacing={1}>
            <FormControlLabel
              control={<Switch checked={collectsCash} onChange={(e) => setCollectsCash(e.target.checked)} />}
              label={t('courierModule.wizard.collectsCash')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={collectsCash && paidFromCash}
                  disabled={!collectsCash}
                  onChange={(e) => setPaidFromCash(e.target.checked)}
                />
              }
              label={t('courierModule.wizard.paidFromCash')}
            />
            <Typography variant="body2" color="text.secondary">
              {t('courierModule.wizard.cashHelp')}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('courierModule.actions.cancel')}
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={saving}>
            {t('courierModule.actions.back')}
          </Button>
        )}
        {step > 0 && step < 3 && (
          <Button variant="contained" onClick={next} disabled={saving}>
            {isValet && step === 2 ? t('courierModule.actions.saveAndAgreement') : t('courierModule.actions.next')}
          </Button>
        )}
        {step === 3 && (
          <>
            <Button onClick={() => save(false)} disabled={saving}>
              {t('courierModule.actions.save')}
            </Button>
            <Button
              variant="contained"
              onClick={() => save(true)}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              {t('courierModule.actions.saveAndAgreement')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCourierWizard;
